/**
 * Top-level Noki overlay — floating button + slide-in panel.
 *
 * Mounted once at the App level via React Portal so it lives above
 * every route and never conflicts with the page tree's z-indexes.
 * Self-contained: zero coupling to noki-fe's existing Redux state,
 * routes, theme, or sidebars. Adding/removing this component is a
 * one-line change in App.jsx.
 *
 * Server URL: defaults to ``http://localhost:7860`` (the standalone
 * Pipecat copilot server). Override with ``REACT_APP_COPILOT_SERVER_URL``
 * in your env file.
 * we are deploy
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { constants } from "redux/constants/redux-constants";
import { NokiCopilotPanel } from "./NokiCopilotPanel";
import { tokens } from "./styles";
import { useCopilotFindAndOpenPatientAction } from "./useCopilotFindAndOpenPatientAction";
import { useCopilotCreatePatientAction } from "./useCopilotCreatePatientAction";
import { useCopilotNavigation } from "./useCopilotNavigation";
import { useCopilotNavigateBackAction } from "./useCopilotNavigateBackAction";
import { useCopilotNotificationsAction } from "./useCopilotNotificationsAction";
import { useCopilotRefreshAction } from "./useCopilotRefreshAction";
import { useCopilotThemeAction } from "./useCopilotThemeAction";
import { useCopilotViewArchivedEncountersAction } from "./useCopilotViewArchivedEncountersAction";
import {
  selectAnyOverlayOpen,
  COPILOT_SETTINGS_PEEK_OVERLAY_ID,
} from "redux/reducers/UiOverlaysSlice";
import { LauncherNote, MicMutedIcon } from "./LauncherNote";

// Empty default = same-origin. CRA's setupProxy.js forwards /api/* and
// /ws/* to the Pipecat server. Override REACT_APP_COPILOT_SERVER_URL only
// if you want to bypass the proxy (e.g. point at a remote server).
const DEFAULT_SERVER_URL = "";

export function NokiCopilot({ isAuthReady = false }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState(null);
  // Session status surfaced up from NokiCopilotPanel. We use it to make
  // the launcher button radiate when the user closes the panel mid-session
  // (session keeps running because the panel mount persists at App level).
  const [sessionStatus, setSessionStatus] = useState("idle");
  const sessionLiveBehindPanel = sessionStatus === "connected" && !open;

  // Overlay-aware auto-collapse: when another right-drawer / popover opens
  // (start-encounter drawer, add-patient drawer, consent drawer, ...) we
  // collapse the visible panel so the user isn't fighting two stacked
  // sheets. The WebRTC session and action-hook registrations live inside
  // NokiCopilotPanel and survive `open === false` — only the visual sheet
  // hides. `stickyOpen` is the "user click wins" escape: once the user
  // explicitly reopens the panel while an overlay is on screen, we stop
  // auto-collapsing until they close it themselves.
  const anyOverlayOpen = useSelector(selectAnyOverlayOpen);
  const [stickyOpen, setStickyOpen] = useState(false);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const prev = prevOpenRef.current;
    // Panel just opened while an overlay was already on screen → the user
    // explicitly chose to reopen; respect that until they close it.
    if (!prev && open && anyOverlayOpen) {
      setStickyOpen(true);
    }
    // Panel just closed → clear the sticky bit so the next overlay-open
    // can auto-collapse again on a fresh cycle.
    if (prev && !open) {
      setStickyOpen(false);
    }
    prevOpenRef.current = open;
  }, [open, anyOverlayOpen]);
  useEffect(() => {
    if (anyOverlayOpen && open && !stickyOpen) {
      setOpen(false);
    }
  }, [anyOverlayOpen, open, stickyOpen]);

  // Peek-and-return: a Default System Setting voice change (conversation
  // language, clinical note, watermark, ...) briefly registers the peek
  // overlay — the effect above collapses the panel so the user sees the
  // value change underneath. When the peek clears we reopen the panel so
  // the user can keep talking. Scoped to the peek id ONLY, so drawers
  // (which intentionally don't auto-reopen) are unaffected.
  const settingsPeekOpen = useSelector((s) =>
    (s?.uiOverlays?.openIds ?? []).includes(COPILOT_SETTINGS_PEEK_OVERLAY_ID),
  );
  const prevPeekRef = useRef(false);
  useEffect(() => {
    const prevPeek = prevPeekRef.current;
    if (prevPeek && !settingsPeekOpen && sessionStatus === "connected") {
      setOpen(true);
    }
    prevPeekRef.current = settingsPeekOpen;
  }, [settingsPeekOpen, sessionStatus]);

  // Auto-pause notice — shown above the launcher when the voice copilot
  // gets muted because an encounter recording is active. The notice
  // explains "why is the mic dark?" without forcing the user to expand
  // the panel just to see the reason.
  //
  // Source of truth for "is recording active" is the same per-encounter
  // map NokiCopilotPanel reads in its mute-policy effect. We re-derive
  // it here at the launcher level so the notice can render even when
  // the panel is fully collapsed. Cheap — Redux selectors short-circuit
  // when the slice hasn't changed.
  const isAnyRecordingActive = useSelector((state) => {
    const byEncounter = state?.recognizer?.recordingByEncounter;
    let hasAnyEntry = false;
    if (byEncounter && typeof byEncounter === "object") {
      for (const key of Object.keys(byEncounter)) {
        hasAnyEntry = true;
        const v = byEncounter[key];
        if (v?.recording === true && v?.paused !== true) {
          return true;
        }
      }
    }
    if (hasAnyEntry) return false;
    // Legacy fallbacks — same precedence as NokiCopilotPanel.
    if (state?.recognizer?.isRecordingInProgress === true) return true;
    if (state?.recordingStatus?.isRecording === true) return true;
    return false;
  });
  // `noteDismissed` survives only for the current mute-active window:
  // dismissing the note (X click OR opening the panel) silences it
  // until the recording stops, then a fresh recording earns a fresh
  // notice. Tracking it as state (not a ref) so React re-renders the
  // bubble when the dismiss flips.
  const [noteDismissed, setNoteDismissed] = useState(false);
  // Reset the dismiss latch the moment recording is no longer active.
  // Next time a recording starts, the user sees the note again.
  useEffect(() => {
    if (!isAnyRecordingActive && noteDismissed) {
      setNoteDismissed(false);
    }
  }, [isAnyRecordingActive, noteDismissed]);
  // Opening the panel is an implicit "I've seen it" — dismiss the
  // note so it doesn't reappear when the panel closes again. Tracking
  // the open→true transition (not just open) so a user who closes the
  // panel without first opening it doesn't latch the dismiss.
  useEffect(() => {
    if (open && !noteDismissed) {
      setNoteDismissed(true);
    }
  }, [open, noteDismissed]);
  const showAutoPauseNote = isAnyRecordingActive && !noteDismissed && !open;

  // Visibility gate. The mount stays at the App level (see App.jsx comment)
  // so the WebRTC session and action-hook registrations survive transient
  // flips of isFirebaseLoaded / isUserDataLoaded. Here we instead gate the
  // *rendered output* with a one-way latch: arm when fully loaded + logged
  // in, disarm only on real logout. This keeps the copilot hidden through
  // boot and login, then sticky against token-refresh flag flips.
  const isUserLoggedIn = useSelector((s) => Boolean(s?.auth?.isUserLoggedIn));
  const [hasFullyLoadedOnce, setHasFullyLoadedOnce] = useState(false);
  useEffect(() => {
    if (isAuthReady && isUserLoggedIn) {
      setHasFullyLoadedOnce(true);
      // Clear any `recording: true` entries that survived a previous
      // session which ended without a clean STOP_RECORDING (crash, hard
      // nav, network drop). Without this, redux-persist would replay the
      // stale flag on the next boot and phantom-mute the voice copilot
      // the moment the user opens it — mic disabled + bot audio silenced
      // even though no recording is actually in progress.
      dispatch({ type: constants.RECOGNIZER.CLEAR_STALE_RECORDINGS });
    } else if (!isUserLoggedIn) {
      setHasFullyLoadedOnce(false);
    }
  }, [isAuthReady, isUserLoggedIn, dispatch]);
  const shouldRender = isUserLoggedIn && hasFullyLoadedOnce;

  // Register the canonical globals. NokiCopilot mounts once at the App
  // level, so these actions are reachable as soon as the user opens the
  // panel — no per-route wiring required.
  useCopilotNavigation();
  useCopilotNavigateBackAction();
  useCopilotRefreshAction();
  useCopilotThemeAction();
  useCopilotViewArchivedEncountersAction();
  useCopilotNotificationsAction();
  useCopilotFindAndOpenPatientAction();
  useCopilotCreatePatientAction();
  // NOTE: settings-page actions (General Settings toggles + Default System
  // Settings dropdowns) are intentionally NOT registered here. They are
  // registered from SettingsPage_v2 so they mount/unmount with the
  // settings route — the agent only has those capabilities while on the
  // Settings page. Theme stays global (changeable from anywhere).

  // Mount a dedicated DOM root for the portal so the overlay can never
  // be unmounted by an ancestor re-render. Cleaned up on unmount.
  useEffect(() => {
    const node = document.createElement("div");
    node.setAttribute("data-noki-copilot-root", "");
    node.style.position = "fixed";
    node.style.inset = "0";
    node.style.pointerEvents = "none";
    node.style.zIndex = "2147483645";
    document.body.appendChild(node);
    setHost(node);
    return () => {
      node.remove();
    };
  }, []);

  if (!host) return null;
  if (!shouldRender) return null;

  const serverUrl =
    process.env.REACT_APP_COPILOT_SERVER_URL || DEFAULT_SERVER_URL;

  const overlay = (
    <>
      <NokiCopilotPanel
        open={open}
        onClose={() => setOpen(false)}
        serverUrl={serverUrl}
        onStatusChange={setSessionStatus}
      />
      <LauncherNote
        visible={showAutoPauseNote}
        onDismiss={() => setNoteDismissed(true)}
        icon={<MicMutedIcon />}
        title="Noki is paused"
        message="An encounter recording is active. Voice copilot will resume when the recording stops."
        tone="info"
      />
      <motion.button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-pressed={open}
        aria-label={open ? "Close Noki copilot" : "Open Noki copilot"}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        animate={{
          boxShadow:
            sessionLiveBehindPanel || open
              ? `${tokens.shadow.glow}, ${tokens.shadow.button}`
              : tokens.shadow.button,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: "none",
          // Match the app's primary action gradient (used by "Start New
          // Encounter" + "Pro" — see .start-new-encounter-bg in dashboard.scss).
          background:
            "linear-gradient(93.39deg, #cb31e2 -5.66%, #8c68c3 56.74%, #6754a7 96.84%)",
          color: tokens.ink[50],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
          pointerEvents: "auto",
          zIndex: 2147483647,
        }}
      >
        {sessionLiveBehindPanel && (
          <>
            <motion.span
              aria-hidden="true"
              animate={{ scale: [1, 1.9], opacity: [0.55, 0] }}
              transition={{ duration: 1.8, ease: "easeOut", repeat: Infinity }}
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `2px solid ${tokens.accent.cyan}`,
                zIndex: -1,
              }}
            />
            <motion.span
              aria-hidden="true"
              animate={{ scale: [1, 1.9], opacity: [0.55, 0] }}
              transition={{
                duration: 1.8,
                ease: "easeOut",
                repeat: Infinity,
                delay: 0.9,
              }}
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `2px solid ${tokens.accent.violet}`,
                zIndex: -1,
              }}
            />
          </>
        )}
        <motion.span
          aria-hidden="true"
          animate={{
            opacity: sessionLiveBehindPanel
              ? [0.75, 1, 0.75]
              : open
              ? [0.35, 0.6, 0.35]
              : [0.55, 0.85, 0.55],
            scale: [1, 1.08, 1],
          }}
          transition={{
            duration: sessionLiveBehindPanel ? 1.4 : 2.6,
            ease: "easeInOut",
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            inset: -6,
            borderRadius: "50%",
            background: `conic-gradient(from 90deg, ${tokens.accent.cyan}33, ${tokens.accent.violet}55, ${tokens.accent.cyan}33)`,
            filter: "blur(8px)",
            zIndex: -1,
          }}
        />
        <img
          src="/logo.svg"
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{
            width: 28,
            height: 28,
            display: "block",
            filter: "brightness(0) invert(1)",
            userSelect: "none",
          }}
        />
      </motion.button>
    </>
  );

  return createPortal(overlay, host);
}
